import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { format } from "date-fns";

import { faRightToBracket } from "@fortawesome/pro-regular-svg-icons";

import { StaffTicketDetail } from "@signalist/shared-types";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { ROUTE_CONFIG } from "@/routes/config";

import { Companies } from "@/pages/Private/pages/Companies/schema/companies";

import { ticketSenderName } from "../../../utils/staffTicketRowMeta";
import { currentPlanName, customerRole } from "../utils/customerCard";
import { SupportUserCandidate } from "../utils/supportUser";
import { RailHeading } from "./RailSection";

interface TicketCustomerCardProps {
	ticket: StaffTicketDetail;
	company: Companies | undefined;
	openTicketCount: number | null;
	supportUser: SupportUserCandidate | null;
	isLoggingIn: boolean;
	onLoginAs: () => void;
}

/**
 * The mockup's "Customer" card (decision 5): the filer and their role, the company with plan and
 * user count, customer since and open tickets, "Log in as" (the company's Signalist support user,
 * decision 3 — disabled with a reason when there is none) and the Support Dashboard.
 */
export const TicketCustomerCard: FunctionComponent<TicketCustomerCardProps> = ({
	ticket,
	company,
	openTicketCount,
	supportUser,
	isLoggingIn,
	onLoginAs,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const none = t("support.staff.detail.rail.none");
	const plan = currentPlanName(company);
	const planLabel = plan
		? t("support.staff.detail.rail.plan", { plan })
		: company
			? t("support.staff.detail.rail.noPlan")
			: none;
	const usersLabel =
		company?.userCount !== undefined
			? t("support.staff.detail.rail.users", { count: company.userCount })
			: none;
	const sinceLabel = company?.createdAt
		? t("support.staff.detail.rail.customerSince", {
				date: format(new Date(company.createdAt), "MMM yyyy"),
			})
		: none;
	const openLabel =
		openTicketCount === null
			? none
			: t("support.staff.detail.rail.openTickets", { count: openTicketCount });

	return (
		<>
			<RailHeading>{t("support.staff.detail.rail.customer")}</RailHeading>
			<div className="mt-1.5 flex flex-col gap-1 rounded-xl border border-gray-200 p-3">
				<span className="text-sm font-semibold text-text">{ticketSenderName(ticket)}</span>
				<span className="text-text-meta">
					{ticket.createdByUserId === null
						? (ticket.createdBy?.email ?? ticket.externalSenderEmail ?? none)
						: `${ticket.createdBy?.email ?? none} · ${t(
								`support.staff.detail.rail.role.${customerRole(company, ticket.createdByUserId)}`
							)}`}
				</span>
				<span className="mt-2 text-text-meta">
					<span className="font-semibold text-text">
						{ticket.companyName ?? company?.name ?? none}
					</span>
					{` · ${planLabel} · ${usersLabel}`}
				</span>
				<span className="text-text-meta">{`${sinceLabel} · ${openLabel}`}</span>
				<div className="mt-2.5 grid grid-cols-2 gap-1.5">
					<Tooltip
						content={
							supportUser
								? t("support.staff.detail.loginAsHint", { email: supportUser.email })
								: t("support.staff.detail.noSupportUser")
						}
					>
						<span className="flex">
							<Button
								className="w-full"
								color={ButtonColor.ACTION_SECONDARY}
								disabled={!supportUser || isLoggingIn}
								image={<Icon className="mr-1.5" icon={faRightToBracket} />}
								isLoading={isLoggingIn}
								size={ButtonSize.XS}
								title={t("support.staff.detail.rail.loginAs")}
								type="button"
								onClick={onLoginAs}
							/>
						</span>
					</Tooltip>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						size={ButtonSize.XS}
						title={t("support.staff.detail.rail.supportDashboard")}
						type="button"
						onClick={() => navigate(ROUTE_CONFIG.SUPPORT_DASHBOARD)}
					/>
				</div>
			</div>
		</>
	);
};
